import { ImagePlus, Info, LoaderCircle, Trash2, UploadCloud } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api, authConfig, formatApiErrorDetail } from "@/lib/api";

const optimizeCloudinaryUrl = (url, width = 1200) => {
  if (!url?.includes("/upload/")) return url;
  return url.replace("/upload/", `/upload/f_auto,q_auto,w_${width},c_limit/`);
};

export const CloudinaryImageField = ({
  compact = false,
  fitGuidance,
  folder,
  helperText,
  label,
  previewAlt,
  previewMode = "cover",
  testIdPrefix,
  value,
  onChange,
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPG, PNG, and WEBP files are supported");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be 10MB or smaller");
      return;
    }

    setUploading(true);
    try {
      const signaturePayload = await api.get("/cloudinary/signature", authConfig({ params: { resource_type: "image", folder } }));
      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", signaturePayload.data.api_key);
      formData.append("timestamp", String(signaturePayload.data.timestamp));
      formData.append("signature", signaturePayload.data.signature);
      formData.append("folder", signaturePayload.data.folder);
      const response = await fetch(`https://api.cloudinary.com/v1_1/${signaturePayload.data.cloud_name}/image/upload`, {
        method: "POST",
        body: formData,
      });
      const asset = await response.json();
      if (!response.ok) {
        throw new Error(asset.error?.message || "Cloudinary upload failed");
      }
      onChange(optimizeCloudinaryUrl(asset.secure_url));
      toast.success(`${label} uploaded successfully`);
    } catch (error) {
      toast.error(error.message || formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  if (compact) {
    return (
      <div className="grid gap-3" data-testid={`${testIdPrefix}-field`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#001935]" data-testid={`${testIdPrefix}-label`}>{label}</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="rounded-sm p-1 text-[#6b6252] transition hover:bg-[#001935]/5 hover:text-[#001935]" data-testid={`${testIdPrefix}-guidance-trigger`} type="button">
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-[#001935] text-[#FDFBF7]" data-testid={`${testIdPrefix}-guidance-tooltip`} side="top">
                    {fitGuidance}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="mt-1 text-xs leading-5 text-[#5e6c7b]" data-testid={`${testIdPrefix}-helper`}>{helperText}</p>
          </div>
          <input
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid={`${testIdPrefix}-file-input`}
            onChange={(event) => {
              uploadFile(event.target.files?.[0]);
              event.target.value = "";
            }}
            ref={fileInputRef}
            type="file"
          />
          <button className="inline-flex h-9 shrink-0 items-center justify-center rounded-sm border border-[#001935]/15 bg-white px-3 text-sm font-medium text-[#001935] transition hover:bg-[#001935]/5" data-testid={`${testIdPrefix}-upload-button`} disabled={uploading} onClick={() => fileInputRef.current?.click()} type="button">
            {uploading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
            {value ? "Replace" : "Upload"}
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-[140px_minmax(0,1fr)]">
          <div className="overflow-hidden rounded-sm border border-dashed border-[#001935]/18 bg-white" data-testid={`${testIdPrefix}-preview-card`}>
            {value ? (
              <img
                alt={previewAlt}
                className={`aspect-[4/3] w-full ${previewMode === "contain" ? "object-contain p-3" : "object-cover"}`}
                data-testid={`${testIdPrefix}-preview-image`}
                src={value}
              />
            ) : (
              <div className="flex aspect-[4/3] items-center justify-center px-3 text-center text-xs text-[#6b6252]" data-testid={`${testIdPrefix}-preview-empty`}>
                No image uploaded
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-sm border border-[#001935]/10 bg-white px-3 py-2 text-xs leading-5 text-[#5e6c7b]" data-testid={`${testIdPrefix}-meta-card`}>
              JPG, PNG, WEBP · max 10MB
            </div>
            {value ? (
              <button className="inline-flex h-9 items-center justify-center rounded-sm border border-red-200 bg-red-50 px-3 text-sm font-medium text-red-600 transition hover:bg-red-100" data-testid={`${testIdPrefix}-remove-button`} onClick={() => onChange("")} type="button">
                <Trash2 className="mr-2 h-4 w-4" /> Remove
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-3" data-testid={`${testIdPrefix}-field`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`${testIdPrefix}-label`}>{label}</p>
          <p className="mt-2 text-sm leading-7 text-[#42566f]" data-testid={`${testIdPrefix}-helper`}>{helperText}</p>
        </div>
        <input
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          className="hidden"
          data-testid={`${testIdPrefix}-file-input`}
          onChange={(event) => {
            uploadFile(event.target.files?.[0]);
            event.target.value = "";
          }}
          ref={fileInputRef}
          type="file"
        />
        <button className="outline-button !px-4 !py-2" data-testid={`${testIdPrefix}-upload-button`} disabled={uploading} onClick={() => fileInputRef.current?.click()} type="button">
          {uploading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
          {value ? "Replace" : "Upload"}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="overflow-hidden rounded-[24px] border border-[#001935]/10 bg-white" data-testid={`${testIdPrefix}-preview-card`}>
          {value ? (
            <img
              alt={previewAlt}
              className={`aspect-square w-full ${previewMode === "contain" ? "object-contain p-6" : "object-cover"}`}
              data-testid={`${testIdPrefix}-preview-image`}
              src={value}
            />
          ) : (
            <div className="flex aspect-square items-center justify-center bg-[#fffaf1] px-6 text-center text-sm leading-7 text-[#6b6252]" data-testid={`${testIdPrefix}-preview-empty`}>
              Upload an image to preview it here.
            </div>
          )}
        </div>

        <div className="rounded-[24px] border border-[#D4AF37]/20 bg-[#fffaf1] p-4" data-testid={`${testIdPrefix}-guidance-card`}>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-white px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid={`${testIdPrefix}-guidance-badge`}>
            <ImagePlus className="h-4 w-4" /> Fit Guidance
          </div>
          <p className="mt-4 text-sm leading-7 text-[#42566f]" data-testid={`${testIdPrefix}-guidance-text`}>{fitGuidance}</p>
          {value ? (
            <button className="mt-4 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100" data-testid={`${testIdPrefix}-remove-button`} onClick={() => onChange("")} type="button">
              <Trash2 className="mr-2 inline h-4 w-4" /> Remove image
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};